/**
 * Search shapes consumed by the frontend. Mirror the backend search module
 * schemas (`SearchModel.searchHit` / `searchPayload`) field-for-field so the
 * Eden-inferred wire type and these domain types stay 1:1.
 */

/** The source channels a hit can come from — matches `SEARCH_SOURCE_CHANNELS`
 * on the backend. Ordered as they appear in the filter bar. */
export const SEARCH_CHANNELS = [
  "transcript",
  "summary",
  "decisions",
  "action_points",
  "open_questions",
  "topics",
  "speaker_quotes",
] as const

export type TSearchChannel = (typeof SEARCH_CHANNELS)[number]

/** Runtime guard so a stray URL param never widens the channel filter silently. */
export function isSearchChannel(value: string): value is TSearchChannel {
  return (SEARCH_CHANNELS as readonly string[]).includes(value)
}

/** `warm` = results are authoritative; `warming` = engine still spinning up. */
export type TSearchStatus = "warm" | "warming"

/** A single scored hit. Mirrors the backend `searchHit` schema exactly. */
export interface ISearchHit {
  chunkId: string
  meetingId: string
  /** Kept as `string` (not `TSearchChannel`) to match the wire type; map to a
   * known channel via a lookup with a fallback at the render boundary. */
  sourceChannel: string
  content: string
  speaker: string | null
  seekStart: number | null
  seekEnd: number | null
  score: number
  meetingSubject: string
  meetingDate: string
}

/** Warm-engine diagnostics returned alongside hits; `null` while the org is cold. */
export interface ISearchEngineStats {
  chunkCount: number
  warmedAtIso: string
}

/** The search payload envelope (the inner `data` of the success response). */
export interface ISearchPayload {
  status: TSearchStatus
  hits: ISearchHit[]
  engine: ISearchEngineStats | null
}
